import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "../lib/apiClient";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("uptoo_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        localStorage.removeItem("uptoo_token");
        toast.info("Your session has ended — please sign in again.");
      } else {
        // Network failure or a browser extension blocking /api: keep the token so a reload can recover, and say why.
        toast.error("Couldn't verify your sign-in. Check your connection or browser blockers, then reload.");
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback((token, userObj) => {
    localStorage.setItem("uptoo_token", token);
    setUser(userObj);
    // If the user hit "Save this search" while signed out, complete the save now.
    const pending = localStorage.getItem("pending_saved_search");
    if (pending) {
      localStorage.removeItem("pending_saved_search");
      try {
        api.post("/saved-searches", JSON.parse(pending))
          .then(({ data }) => toast.success(data.duplicate
            ? "You already saved this search."
            : "Search saved — matches will show in Ding Me."))
          .catch(() => {});
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Ignoring malformed pending_saved_search payload", e); }
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("uptoo_token");
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, setUser, login, logout, refresh }), [user, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
